import { useState, useEffect } from "react";
import { 
  ScrollText, 
  AlertTriangle, 
  Info,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  Filter,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventLog = () => {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    event_type: '',
    severity: '',
    days: 7
  });

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('days', filters.days);
      params.append('limit', 100);
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.severity) params.append('severity', filters.severity);
      
      const [eventsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/events?${params}`),
        axios.get(`${API}/events/summary?days=${filters.days}`)
      ]);
      
      setEvents(eventsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchEvents();
    }
  }, [open, filters]);

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-amber-400" />;
      case 'info': return <Info className="w-4 h-4 text-blue-400" />;
      default: return <CheckCircle2 className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'error': return 'bg-red-500/10 text-red-300 border-red-500/20';
      case 'warning': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'info': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const getEventTypeLabel = (type) => {
    const labels = {
      'sensor_update': 'Actualización Sensor',
      'config_change': 'Cambio Configuración',
      'alert': 'Alerta',
      'login': 'Inicio Sesión',
      'user_action': 'Acción Usuario',
      'system': 'Sistema'
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Button 
        variant="ghost" 
        size="icon"
        onClick={() => setOpen(true)}
        className="text-zinc-400 hover:text-zinc-100 h-10 w-10 p-0"
        data-testid="event-log-btn"
      >
        <ScrollText className="w-[44px] h-[44px]" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] bg-zinc-900 border-zinc-800 p-4">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100 flex items-center gap-2">
              <ScrollText className="w-8 h-8 text-blue-400" />
              Registro de Eventos
            </DialogTitle>
          </DialogHeader>

          {/* Summary Stats */}
          {summary && (
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="bg-zinc-800 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">{summary.total || 0}</div>
                <div className="text-xs text-zinc-400">Total Eventos</div>
              </div>
              <div className="bg-red-500/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-400">
                  {(summary.by_severity?.critical || 0) + (summary.by_severity?.error || 0)}
                </div>
                <div className="text-xs text-zinc-400">Críticos</div>
              </div>
              <div className="bg-amber-500/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-amber-400">{summary.by_severity?.warning || 0}</div>
                <div className="text-xs text-zinc-400">Alertas</div>
              </div>
              <div className="bg-blue-500/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-400">{summary.by_severity?.info || 0}</div>
                <div className="text-xs text-zinc-400">Info</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-2 mb-4">
            <Select value={filters.severity || "all"} onValueChange={(v) => setFilters(f => ({ ...f, severity: v === "all" ? '' : v }))}>
              <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700 text-zinc-100">
                <SelectValue placeholder="Severidad" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="critical">Crítico</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warning">Alerta</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.event_type || "all"} onValueChange={(v) => setFilters(f => ({ ...f, event_type: v === "all" ? '' : v }))}>
              <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700 text-zinc-100">
                <SelectValue placeholder="Tipo de Evento" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="alert">Alertas</SelectItem>
                <SelectItem value="sensor_update">Sensores</SelectItem>
                <SelectItem value="config_change">Configuración</SelectItem>
                <SelectItem value="login">Sesiones</SelectItem>
                <SelectItem value="system">Sistema</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={String(filters.days)} onValueChange={(v) => setFilters(f => ({ ...f, days: parseInt(v) }))}>
              <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-zinc-100">
                <SelectValue placeholder="Periodo" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="1">Hoy</SelectItem>
                <SelectItem value="7">7 días</SelectItem>
                <SelectItem value="30">30 días</SelectItem>
                <SelectItem value="90">90 días</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={fetchEvents}
              disabled={loading}
              className="text-zinc-400 hover:text-zinc-100"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Events List */}
          <div className="overflow-y-auto max-h-[50vh] space-y-2">
            {loading ? (
              <div className="text-center py-8 text-zinc-400">Cargando eventos...</div>
            ) : events.length === 0 ? (
              <div className="text-center py-8 text-zinc-400">No hay eventos registrados</div>
            ) : (
              events.map((event) => (
                <div 
                  key={event.id} 
                  className={`p-3 rounded-lg border ${getSeverityColor(event.severity)}`}
                >
                  <div className="flex items-start gap-3">
                    {getSeverityIcon(event.severity)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-white truncate">
                          {event.message}
                        </span>
                        <span className="text-xs text-zinc-500 whitespace-nowrap">
                          {formatDate(event.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">
                          {getEventTypeLabel(event.event_type)}
                        </span>
                        {event.source_name && (
                          <span className="text-xs text-zinc-500">
                            {event.source_name}
                          </span>
                        )}
                        {event.user_name && (
                          <span className="text-xs text-zinc-500">
                            por {event.user_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EventLog;
